import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// Choice archetype — single checkbox (consent-style), radio group, or
// select. Picks variant from config.variant. Config:
//   - payloadKey  → dotted-path target on the draft
//   - label       → field label
//   - variant     → "checkbox" | "radio" | "select"
//   - options[]   → for radio/select: [{ label, value }]
//   - required    → at least one selection
//   - hint        → optional helper text (used by checkbox for consent copy)
import { useState } from 'react';
import { useStageForm } from '../runtime/useStageForm.js';
import { s, b } from './common.js';
function readOptions(config) {
    const raw = config.options;
    if (!Array.isArray(raw))
        return [];
    return raw.flatMap((o) => {
        if (!o || typeof o !== 'object')
            return [];
        const ob = o;
        if (typeof ob.label === 'string' && typeof ob.value === 'string') {
            return [{ label: ob.label, value: ob.value }];
        }
        return [];
    });
}
function readInitialString(payload, path) {
    const parts = path.split('.');
    let cur = payload;
    for (const part of parts) {
        if (cur && typeof cur === 'object' && part in cur) {
            cur = cur[part];
        }
        else
            return '';
    }
    return typeof cur === 'string' ? cur : '';
}
function readInitialBool(payload, path) {
    const parts = path.split('.');
    let cur = payload;
    for (const part of parts) {
        if (cur && typeof cur === 'object' && part in cur) {
            cur = cur[part];
        }
        else
            return false;
    }
    return cur === true;
}
export function ChoiceComponent(props) {
    const { config, payload, registry, blockId } = props;
    const payloadKey = s(config, 'payloadKey');
    const label = s(config, 'label');
    const hint = s(config, 'hint');
    const variant = s(config, 'variant', 'checkbox');
    const required = b(config, 'required', false);
    const options = readOptions(config);
    if (variant === 'checkbox') {
        return (_jsx(CheckboxField, { blockId: blockId, registry: registry, payloadKey: payloadKey, label: label, hint: hint, required: required, initial: readInitialBool(payload, payloadKey) }));
    }
    return (_jsx(PickerField, { blockId: blockId, registry: registry, payloadKey: payloadKey, label: label, hint: hint, variant: variant === 'select' ? 'select' : 'radio', required: required, options: options, initial: readInitialString(payload, payloadKey) }));
}
function CheckboxField(props) {
    const [checked, setChecked] = useState(props.initial);
    useStageForm(props.registry, props.blockId, {
        payloadKey: props.payloadKey,
        value: checked,
        validator: (v) => (props.required && !v ? 'Required' : null),
    });
    return (_jsxs("label", { className: "grid cursor-pointer grid-cols-[auto_1fr] items-start gap-2.5 text-sm", children: [_jsx("input", { type: "checkbox", className: "mt-1 h-4 w-4 accent-[var(--bcbb-primary)]", checked: checked, onChange: (e) => setChecked(e.target.checked) }), _jsxs("span", { children: [props.label, props.hint && (_jsx("span", { className: "mt-1 block text-xs text-[var(--bcbb-text-muted)]", children: props.hint }))] })] }));
}
function PickerField(props) {
    const [value, setValue] = useState(props.initial);
    useStageForm(props.registry, props.blockId, {
        payloadKey: props.payloadKey,
        value: value === '' ? undefined : value,
        validator: (v) => (props.required && !v ? 'Required' : null),
    });
    if (props.variant === 'select') {
        return (_jsxs("label", { className: "flex flex-col gap-1.5", children: [_jsxs("span", { className: "text-xs font-semibold text-[var(--bcbb-text)]", children: [props.label, props.required ? ' *' : ''] }), _jsxs("select", { value: value, onChange: (e) => setValue(e.target.value), className: "rounded-md border bg-white px-3 py-2 text-sm", style: { borderColor: 'var(--bcbb-border)', color: 'var(--bcbb-text)' }, children: [_jsx("option", { value: "", children: props.required ? 'Select…' : '— none —' }), props.options.map((o) => (_jsx("option", { value: o.value, children: o.label }, o.value)))] }), props.hint && _jsx("span", { className: "text-xs text-[var(--bcbb-text-muted)]", children: props.hint })] }));
    }
    return (_jsxs("fieldset", { className: "flex flex-col gap-2", children: [_jsxs("legend", { className: "text-xs font-semibold text-[var(--bcbb-text)]", children: [props.label, props.required ? ' *' : ''] }), props.options.map((o) => (_jsxs("label", { className: `flex cursor-pointer items-center justify-between gap-3 rounded-md border-2 bg-white px-3 py-2 text-sm ${value === o.value ? 'border-[var(--bcbb-comms)]' : 'border-[var(--bcbb-border)]'}`, children: [_jsx("span", { children: o.label }), _jsx("input", { type: "radio", name: props.payloadKey, value: o.value, checked: value === o.value, onChange: () => setValue(o.value), className: "h-4 w-4 accent-[var(--bcbb-comms)]" })] }, o.value))), props.hint && _jsx("span", { className: "text-xs text-[var(--bcbb-text-muted)]", children: props.hint })] }));
}
export const ChoiceArchetype = {
    name: 'Choice',
    label: 'Choice',
    description: 'Checkbox (consent), radio group, or select dropdown.',
    component: ChoiceComponent,
    isFormArchetype: true,
    fields: {
        payloadKey: { kind: 'text', label: 'Payload key (dotted path)' },
        label: { kind: 'text', label: 'Label' },
        hint: { kind: 'textarea', label: 'Hint / consent copy', optional: true },
        variant: {
            kind: 'select',
            label: 'Variant',
            options: [
                { label: 'Checkbox', value: 'checkbox' },
                { label: 'Radio group', value: 'radio' },
                { label: 'Select dropdown', value: 'select' },
            ],
            defaultValue: 'checkbox',
        },
        required: { kind: 'checkbox', label: 'Required' },
    },
    defaultConfig: {
        payloadKey: '',
        label: 'Choice',
        hint: '',
        variant: 'checkbox',
        required: false,
        options: [],
    },
};
